<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import type { Trip, Expense, Companion, Settlement, ExpenseCategory, ExpenseParticipant } from "../../lib/types";
  import { expensesApi, tripsApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import Modal from "../../components/overlay/Modal.svelte";
  import CompanionManager from "../../components/social/CompanionManager.svelte";
  import ExpenseParticipantSelector from "../../components/data-display/ExpenseParticipantSelector.svelte";
  import SettlementSummary from "../../components/data-display/SettlementSummary.svelte";
  import ExchangeRateManager from "../../components/data-display/ExchangeRateManager.svelte";
  import { categoryIcons } from "../../lib/icons";
  import { formatCurrency } from "../../lib/utils/format";

  let { trip, expenses = [], companions = [], settlement = null, isOwner = false, user = null }: {
    trip: Trip;
    expenses?: Expense[];
    companions?: Companion[];
    settlement?: Settlement | null;
    isOwner?: boolean;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  // Participant selector state
  let showParticipantSelector = $state(false);
  let selectedExpenseId = $state<string | null>(null);
  let selectedExpenseParticipants = $state<ExpenseParticipant[]>([]);

  let selectedIds = $state<Set<string>>(new Set());
  let selectAll = $state(false);
  let editingCell = $state<{ id: string; field: string } | null>(null);
  let editValue = $state("");
  let sortField = $state<keyof Expense>("transactedAt");
  let sortDir = $state<"asc" | "desc">("desc");
  let filterCategory = $state<ExpenseCategory | "">("");
  let searchQuery = $state("");
  let showBulkModal = $state(false);
  let bulkAction = $state("");
  let bulkCategory = $state<ExpenseCategory | "">("");
  let loading = $state(false);
  let showExchangeRateManager = $state(false);

  const categories: Array<{ key: ExpenseCategory; label: string; icon: string; color: string }> = [
    { key: "food", label: "식비", icon: categoryIcons.food, color: "bg-orange-100 text-orange-700" },
    { key: "transport", label: "교통", icon: categoryIcons.transport, color: "bg-blue-100 text-blue-700" },
    { key: "accommodation", label: "숙소", icon: categoryIcons.accommodation, color: "bg-purple-100 text-purple-700" },
    { key: "shopping", label: "쇼핑", icon: categoryIcons.shopping, color: "bg-pink-100 text-pink-700" },
    { key: "activity", label: "액티비티", icon: categoryIcons.activity, color: "bg-green-100 text-green-700" },
    { key: "other", label: "기타", icon: categoryIcons.other, color: "bg-surface-hover text-secondary" },
  ];

  // Filtered and sorted expenses
  let filteredExpenses = $derived(expenses
    .filter(e => {
      if (filterCategory && e.category !== filterCategory) return false;
      if (searchQuery && !e.merchantName.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let aVal: string | number = (a[sortField] as string | number | undefined) ?? "";
      let bVal: string | number = (b[sortField] as string | number | undefined) ?? "";
      if (sortField === "transactedAt" || sortField === "createdAt") {
        aVal = new Date(aVal as string).getTime();
        bVal = new Date(bVal as string).getTime();
      }
      if (sortField === "originalAmountCents" || sortField === "krwAmountCents") {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      if (sortDir === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    }));

  // Stats
  let totalAmount = $derived(filteredExpenses.reduce((sum, e) => sum + e.krwAmountCents, 0));
  let categoryStats = $derived(categories.map(cat => ({
    ...cat,
    amount: filteredExpenses.filter(e => e.category === cat.key).reduce((sum, e) => sum + e.krwAmountCents, 0),
    count: filteredExpenses.filter(e => e.category === cat.key).length,
  })));

  function toggleSelect(id: string) {
    if (selectedIds.has(id)) {
      selectedIds.delete(id);
    } else {
      selectedIds.add(id);
    }
    selectedIds = selectedIds;
    selectAll = selectedIds.size === filteredExpenses.length;
  }

  function toggleSelectAll() {
    if (selectAll) {
      selectedIds.clear();
    } else {
      filteredExpenses.forEach(e => selectedIds.add(e.id));
    }
    selectedIds = selectedIds;
    selectAll = !selectAll;
  }

  function startEdit(id: string, field: string, value: any) {
    editingCell = { id, field };
    editValue = String(value);
  }

  async function saveEdit() {
    if (!editingCell) return;
    
    const { id, field } = editingCell;
    let updateData: any = {};
    
    if (field === "merchantName") {
      updateData.merchantName = editValue;
    } else if (field === "originalAmountCents") {
      updateData.originalAmountCents = Math.round(parseFloat(editValue) * 100);
    } else if (field === "category") {
      updateData.category = editValue;
    }

    try {
      await expensesApi.update(id, updateData);
      addNotification("success", "수정되었습니다.");
      router.reload();
    } catch (error) {
      addNotification("error", "수정에 실패했습니다.");
    }
    
    editingCell = null;
  }

  function cancelEdit() {
    editingCell = null;
    editValue = "";
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Enter") saveEdit();
    if (event.key === "Escape") cancelEdit();
  }

  async function executeBulkAction() {
    if (selectedIds.size === 0) return;
    
    loading = true;
    const ids = Array.from(selectedIds);
    
    try {
      if (bulkAction === "delete") {
        for (const id of ids) {
          await expensesApi.delete(id);
        }
        addNotification("success", `${ids.length}개 항목이 삭제되었습니다.`);
      } else if (bulkAction === "category" && bulkCategory) {
        for (const id of ids) {
          await expensesApi.update(id, { category: bulkCategory as ExpenseCategory });
        }
        addNotification("success", `${ids.length}개 항목의 카테고리가 변경되었습니다.`);
      }
      
      selectedIds.clear();
      selectedIds = selectedIds;
      showBulkModal = false;
      router.reload();
    } catch (error) {
      addNotification("error", "일괄 작업에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric",
      weekday: "short",
    });
  }

  function getCategoryInfo(key: string) {
    return categories.find(c => c.key === key) || categories[5];
  }

  function sort(field: keyof Expense) {
    if (sortField === field) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
    } else {
      sortField = field;
      sortDir = "desc";
    }
  }

  function openParticipantSelector(expense: Expense) {
    selectedExpenseId = expense.id;
    selectedExpenseParticipants = expense.participants || [];
    showParticipantSelector = true;
  }

  function closeParticipantSelector() {
    showParticipantSelector = false;
    selectedExpenseId = null;
    selectedExpenseParticipants = [];
  }

  function handleParticipantsSaved() {
    router.reload();
  }

  function handleCompanionsChange() {
    router.reload();
  }

  async function exportExcel() {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/expenses/export.xlsx`, {
        headers: { "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${trip.name}_지출내역.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      addNotification("success", "Excel 파일이 다운로드되었습니다.");
    } catch (error) {
      addNotification("error", "다운로드에 실패했습니다.");
    }
  }

  async function exportPDF() {
    try {
      const response = await fetch(`/api/v1/trips/${trip.id}/expenses/export.pdf`, {
        headers: { "Accept": "application/pdf" }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${trip.name}_정산서.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      addNotification("success", "PDF 파일이 다운로드되었습니다.");
    } catch (error) {
      addNotification("error", "다운로드에 실패했습니다.");
    }
  }
</script>

<svelte:window onkeydown={editingCell ? handleKeydown : undefined} />

<Layout {user}>
  <div class="min-h-screen bg-app">
    <!-- Trip Sub Navigation -->
    <TripSubNav {trip} currentPage="finance" />
    
    <!-- Export Buttons -->
    <div class="bg-surface border-b border-default">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-primary flex items-center gap-2">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            총무 모드
          </h2>
        <div class="flex items-center gap-3">
          <button
            onclick={() => showExchangeRateManager = true}
            class="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            환율설정
          </button>
          <button
            onclick={exportExcel}
            class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>
          <button
            onclick={exportPDF}
            class="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            PDF
          </button>
        </div>
      </div>
    </div>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Companion Manager & Settlement Summary -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <CompanionManager 
          tripId={trip.id} 
          {companions} 
          {isOwner}
          onChange={handleCompanionsChange}
        />
        <SettlementSummary 
          tripId={trip.id} 
          {settlement}
        />
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div class="col-span-2 md:col-span-1 bg-surface rounded-xl p-4 shadow-sm">
          <p class="text-xs text-muted">총 지출</p>
          <p class="text-xl font-bold text-primary">₩{formatCurrency(totalAmount)}</p>
          <p class="text-xs text-muted">{filteredExpenses.length}건</p>
        </div>
        {#each categoryStats as cat}
          <button
            onclick={() => filterCategory = filterCategory === cat.key ? "" : cat.key}
            class="bg-surface rounded-xl p-4 shadow-sm text-left transition-all hover:shadow-md
              {filterCategory === cat.key ? 'ring-2 ring-blue-500' : ''}"
          >
            <p class="text-xs text-muted flex items-center gap-1">
              <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">{@html cat.icon}</svg> {cat.label}
            </p>
            <p class="text-lg font-bold text-primary">₩{formatCurrency(cat.amount)}</p>
            <p class="text-xs text-muted">{cat.count}건</p>
          </button>
        {/each}
      </div>

      <!-- Toolbar -->
      <div class="bg-surface rounded-xl shadow-sm p-4 mb-4">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Search -->
          <div class="relative flex-1 min-w-[200px]">
            <input
              type="text"
              bind:value={searchQuery}
              placeholder="가맹점명 검색..."
              class="w-full pl-10 pr-4 py-2 border border-bright rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          <!-- Bulk Actions -->
          {#if selectedIds.size > 0}
            <div class="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
              <span class="text-sm text-blue-700 font-medium">{selectedIds.size}개 선택</span>
              <button
                onclick={() => { bulkAction = "category"; showBulkModal = true; }}
                class="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                카테고리 변경
              </button>
              <button
                onclick={() => { bulkAction = "delete"; showBulkModal = true; }}
                class="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                삭제
              </button>
            </div>
          {/if}

          <!-- Filter Reset -->
          {#if filterCategory || searchQuery}
            <button
              onclick={() => { filterCategory = ""; searchQuery = ""; }}
              class="text-sm text-muted hover:text-secondary"
            >
              필터 초기화
            </button>
          {/if}
        </div>
      </div>

      <!-- Spreadsheet Table -->
      <div class="bg-surface rounded-xl shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-app border-b border-bright">
              <tr>
                <th class="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onchange={toggleSelectAll}
                    class="rounded border-bright text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th class="px-4 py-3 text-left">
                  <button onclick={() => sort("transactedAt")} class="flex items-center gap-1 text-xs font-medium text-muted uppercase tracking-wider hover:text-secondary">
                    날짜
                    {#if sortField === "transactedAt"}
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={sortDir === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                      </svg>
                    {/if}
                  </button>
                </th>
                <th class="px-4 py-3 text-left">
                  <button onclick={() => sort("merchantName")} class="flex items-center gap-1 text-xs font-medium text-muted uppercase tracking-wider hover:text-secondary">
                    가맹점
                    {#if sortField === "merchantName"}
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={sortDir === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                      </svg>
                    {/if}
                  </button>
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">카테고리</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-muted uppercase tracking-wider">결제자</th>
                <th class="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">참여자</th>
                <th class="px-4 py-3 text-right">
                  <button onclick={() => sort("krwAmountCents")} class="flex items-center gap-1 text-xs font-medium text-muted uppercase tracking-wider hover:text-secondary ml-auto">
                    금액
                    {#if sortField === "krwAmountCents"}
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={sortDir === "asc" ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                      </svg>
                    {/if}
                  </button>
                </th>
                <th class="px-4 py-3 text-center text-xs font-medium text-muted uppercase tracking-wider">상태</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-border">
              {#each filteredExpenses as expense (expense.id)}
                {@const catInfo = getCategoryInfo(expense.category)}
                <tr class="hover:bg-surface-hover transition-colors {selectedIds.has(expense.id) ? 'bg-blue-50' : ''}">
                  <td class="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(expense.id)}
                      onchange={() => toggleSelect(expense.id)}
                      class="rounded border-bright text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td class="px-4 py-3 text-sm text-secondary">
                    {formatDate(expense.transactedAt)}
                  </td>
                  <td class="px-4 py-3">
                    {#if editingCell?.id === expense.id && editingCell?.field === "merchantName"}
                      <!-- svelte-ignore a11y_autofocus -->
                      <input
                        type="text"
                        bind:value={editValue}
                        class="w-full px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                        autofocus
                      />
                    {:else}
                      <button
                        ondblclick={() => startEdit(expense.id, "merchantName", expense.merchantName)}
                        class="text-left font-medium text-primary hover:text-blue-600 cursor-text"
                        title="더블클릭하여 수정"
                      >
                        {expense.merchantName}
                      </button>
                    {/if}
                  </td>
                  <td class="px-4 py-3">
                    {#if editingCell?.id === expense.id && editingCell?.field === "category"}
                      <select
                        bind:value={editValue}
                        onchange={saveEdit}
                        class="px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        {#each categories as cat}
                          <option value={cat.key}>{cat.icon} {cat.label}</option>
                        {/each}
                      </select>
                    {:else}
                      <button
                        ondblclick={() => startEdit(expense.id, "category", expense.category)}
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium {catInfo.color} hover:opacity-80"
                        title="더블클릭하여 수정"
                      >
                        {catInfo.icon} {catInfo.label}
                      </button>
                    {/if}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <div class="w-6 h-6 rounded-full bg-surface-hover flex items-center justify-center text-xs font-medium">
                        {expense.payer.name.charAt(0)}
                      </div>
                      <span class="text-sm text-secondary">{expense.payer.name}</span>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-center">
                    <button
                      onclick={() => openParticipantSelector(expense)}
                      class="inline-flex items-center gap-1 px-2 py-1 bg-surface-hover hover:bg-blue-100 text-secondary hover:text-blue-700 rounded-lg text-xs font-medium transition-colors"
                      title="참여자 수정"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {expense.participants?.length || companions.length}명
                    </button>
                  </td>
                  <td class="px-4 py-3 text-right">
                    {#if editingCell?.id === expense.id && editingCell?.field === "originalAmountCents"}
                      <!-- svelte-ignore a11y_autofocus -->
                      <input
                        type="number"
                        bind:value={editValue}
                        class="w-24 px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 text-right"
                        autofocus
                      />
                    {:else}
                      <button
                        ondblclick={() => startEdit(expense.id, "originalAmountCents", expense.originalAmountCents / 100)}
                        class="font-medium text-primary hover:text-blue-600 cursor-text"
                        title="더블클릭하여 수정"
                      >
                        {expense.originalCurrency === "KRW" ? "₩" : expense.originalCurrency} {formatCurrency(expense.originalAmountCents)}
                      </button>
                      {#if expense.originalCurrency !== "KRW"}
                        <p class="text-xs text-muted">≈ ₩{formatCurrency(expense.krwAmountCents)}</p>
                      {/if}
                    {/if}
                  </td>
                  <td class="px-4 py-3 text-center">
                    <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                      {expense.settlementStatus === 'settled' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}">
                      {expense.settlementStatus === 'settled' ? '정산완료' : '미정산'}
                    </span>
                  </td>
                </tr>
              {/each}
            </tbody>
            <tfoot class="bg-app border-t-2 border-bright">
              <tr>
                <td colspan="6" class="px-4 py-3 text-sm font-medium text-secondary">
                  합계 ({filteredExpenses.length}건)
                </td>
                <td class="px-4 py-3 text-right text-lg font-bold text-primary">
                  ₩{formatCurrency(totalAmount)}
                </td>
                <td colspan="2"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Help Text -->
      <p class="text-center text-sm text-muted mt-4">
        <svg class="w-4 h-4 inline-block mr-1 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg> 셀을 더블클릭하면 바로 수정할 수 있습니다. Enter로 저장, Esc로 취소.
      </p>
    </main>
  </div>

  <!-- Bulk Action Modal -->
  <Modal show={showBulkModal} title={bulkAction === "delete" ? "일괄 삭제" : "카테고리 일괄 변경"} onClose={() => showBulkModal = false}>
    <div class="space-y-4">
      {#if bulkAction === "delete"}
        <p class="text-secondary">
          선택한 <strong>{selectedIds.size}개</strong> 항목을 삭제하시겠습니까?
          <br />이 작업은 되돌릴 수 없습니다.
        </p>
      {:else}
        <p class="text-secondary mb-4">
          선택한 <strong>{selectedIds.size}개</strong> 항목의 카테고리를 변경합니다.
        </p>
        <div class="grid grid-cols-3 gap-2">
          {#each categories as cat}
            <button
              onclick={() => bulkCategory = cat.key}
              class="p-3 rounded-lg text-center border-2 transition-colors
                {bulkCategory === cat.key ? 'border-blue-500 bg-blue-50' : 'border-bright hover:border-bright'}"
            >
              <span class="text-xl">{cat.icon}</span>
              <p class="text-xs mt-1">{cat.label}</p>
            </button>
          {/each}
        </div>
      {/if}

      <div class="flex gap-3 pt-4">
        <button
          onclick={() => showBulkModal = false}
          class="flex-1 py-3 bg-surface-hover text-secondary rounded-xl font-medium hover:bg-surface-hover"
        >
          취소
        </button>
        <button
          onclick={executeBulkAction}
          disabled={loading || (bulkAction === "category" && !bulkCategory)}
          class="flex-1 py-3 rounded-xl font-medium disabled:opacity-50
            {bulkAction === 'delete' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-blue-600 text-white hover:bg-blue-700'}"
        >
          {loading ? "처리 중..." : bulkAction === "delete" ? "삭제" : "변경"}
        </button>
      </div>
    </div>
  </Modal>

  <!-- Expense Participant Selector -->
  {#if selectedExpenseId}
    <ExpenseParticipantSelector
      expenseId={selectedExpenseId}
      {companions}
      currentParticipants={selectedExpenseParticipants}
      show={showParticipantSelector}
      onClose={closeParticipantSelector}
      onSave={handleParticipantsSaved}
    />
  {/if}

  <!-- Exchange Rate Manager -->
  <ExchangeRateManager
    tripId={trip.id}
    customRates={trip.customExchangeRates || {}}
    bind:show={showExchangeRateManager}
  />
</Layout>
